import React, { useState, useEffect, useCallback } from 'react';
import { ArrowRight, ArrowLeft, Search, Globe, Link2, LogIn, LogOut, RefreshCw, Download, ExternalLink } from 'lucide-react';
import ModuleVisibilityToggle from './ModuleVisibilityToggle';
import config from '../config';

const API_URL = config.API_URL;

/**
 * MatomoTransitionsChart - Visualise les transitions de pages Matomo
 * Affiche d'où viennent les visiteurs et où ils vont
 */
const MatomoTransitionsChart = ({
  token,
  siteId,
  period,
  customStartDate,
  customEndDate,
  formatNumber,
  userRole = 'USER',
  isClientModuleVisible,
  toggleClientModuleVisibility,
  moduleId = 'matomo_transitions'
}) => {
  const [pages, setPages] = useState([]);
  const [urlToTitle, setUrlToTitle] = useState({});  // Map URL -> Titre
  const [selectedPage, setSelectedPage] = useState('');
  const [selectedPageLabel, setSelectedPageLabel] = useState('');
  const [transitions, setTransitions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingPages, setLoadingPages] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Fonction pour sélectionner une page
  const selectPage = (url, label) => {
    setSelectedPage(url);
    // Chercher le titre dans la liste des pages si disponible
    const pageInfo = pages.find(p => p.url === url || p.label === url);
    if (pageInfo) {
      setSelectedPageLabel(pageInfo.label);
    } else {
      // Sinon utiliser le label fourni ou l'URL
      setSelectedPageLabel(label || url);
    }
  };

  // Charger la liste des pages
  const fetchPages = useCallback(async () => {
    if (!siteId || !token) return;

    setLoadingPages(true);
    try {
      const response = await fetch(`${API_URL}/api/dashboard/matomo/pages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          siteId,
          period,
          customStartDate,
          customEndDate
        })
      });

      const data = await response.json();
      if (data.success) {
        setPages(data.pages || []);
        setUrlToTitle(data.urlToTitle || {});
      }
    } catch (err) {
      console.error('[TRANSITIONS] Erreur chargement pages:', err);
    } finally {
      setLoadingPages(false);
    }
  }, [siteId, token, period, customStartDate, customEndDate]);

  // Charger les transitions pour une page
  const fetchTransitions = useCallback(async (pageUrl) => {
    if (!siteId || !token || !pageUrl) return;

    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/dashboard/matomo/transitions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          siteId,
          pageUrl,
          period,
          customStartDate,
          customEndDate
        })
      });

      const data = await response.json();
      if (data.success) {
        setTransitions(data.transitions);
      } else {
        setError(data.error || 'Erreur lors du chargement');
      }
    } catch (err) {
      console.error('Erreur chargement transitions:', err);
      setError('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  }, [siteId, token, period, customStartDate, customEndDate]);

  // Charger les pages au montage
  useEffect(() => {
    fetchPages();
  }, [fetchPages]);

  // Charger les transitions quand une page est sélectionnée
  useEffect(() => {
    if (selectedPage) {
      fetchTransitions(selectedPage);
    }
  }, [selectedPage, fetchTransitions]);

  // Filtrer les pages selon la recherche
  const filteredPages = pages.filter(p =>
    p.label?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.url?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Extraire les données de transitions
  const getPreviousPages = () => transitions?.previousPages || [];
  const getFollowingPages = () => transitions?.followingPages || [];
  const getPreviousSiteSearches = () => transitions?.previousSiteSearches || [];
  const getFollowingSiteSearches = () => transitions?.followingSiteSearches || [];
  const getReferrers = () => transitions?.referrers || [];
  const getOutlinks = () => transitions?.outlinks || [];
  const getDownloads = () => transitions?.downloads || [];

  // Calculer les totaux
  const pageMetrics = transitions?.pageMetrics || {};
  const totalPageviews = pageMetrics.pageviews || 0;
  const totalExits = pageMetrics.exits || 0;
  const totalLoops = pageMetrics.loops || 0;

  // Calculer le total des entrées
  const totalFromPreviousPages = getPreviousPages().reduce((sum, p) => sum + (p.referrals || 0), 0);
  const totalFromSearches = getPreviousSiteSearches().reduce((sum, p) => sum + (p.referrals || 0), 0);
  const totalFromReferrers = getReferrers().reduce((sum, r) => sum + (r.visits || 0), 0);
  const totalEntries = totalFromPreviousPages + totalFromSearches + totalFromReferrers;

  // Calculer le total des sorties
  const totalToFollowingPages = getFollowingPages().reduce((sum, p) => sum + (p.referrals || 0), 0);
  const totalToSearches = getFollowingSiteSearches().reduce((sum, p) => sum + (p.referrals || 0), 0);
  const totalToOutlinks = getOutlinks().reduce((sum, o) => sum + (o.referrals || 0), 0);
  const totalToDownloads = getDownloads().reduce((sum, d) => sum + (d.referrals || 0), 0);

  // Composant pour une section de transition
  const TransitionSection = ({ title, icon: Icon, items, color, direction, labelKey = 'label', countKey = 'referrals', clickable = false }) => {
    if (!items || items.length === 0) return null;

    const total = items.reduce((sum, item) => sum + (item[countKey] || 0), 0);

    const handleItemClick = (item) => {
      // L'API Transitions retourne l'URL dans "label", pas dans "url"
      const pageUrl = item.url || item.label;
      if (clickable && pageUrl) {
        // Chercher le titre dans la map
        const pageTitle = urlToTitle[item.label] || urlToTitle[`https://${item.label}`] || item.label;
        selectPage(pageUrl, pageTitle);
      }
    };

    return (
      <div style={{ marginBottom: '16px' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '8px',
          color: '#6b7280',
          fontSize: '0.85em',
          fontWeight: '600'
        }}>
          <Icon size={14} />
          <span>{title}</span>
          <span style={{ marginLeft: 'auto', color }}>{formatNumber(total)}</span>
        </div>
        {items.slice(0, 5).map((item, index) => {
          const count = item[countKey] || 0;
          const percentage = total > 0 ? (count / total * 100).toFixed(1) : 0;
          const pageUrl = item.url || item.label;
          const isClickable = clickable && pageUrl;

          // Chercher le titre dans la map urlToTitle
          const rawLabel = item[labelKey] || '';
          const displayLabel = urlToTitle[rawLabel] || urlToTitle[`https://${rawLabel}`] || rawLabel || 'Inconnu';

          return (
            <div
              key={index}
              onClick={() => handleItemClick(item)}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '8px',
                padding: '8px 10px',
                marginBottom: '4px',
                backgroundColor: '#f9fafb',
                borderRadius: '6px',
                fontSize: '0.8em',
                cursor: isClickable ? 'pointer' : 'default',
                transition: 'background-color 0.15s'
              }}
              onMouseEnter={(e) => isClickable && (e.currentTarget.style.backgroundColor = '#e5e7eb')}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
            >
              {direction === 'left' && (
                <ArrowRight size={12} style={{ color: '#9ca3af', flexShrink: 0, marginTop: '2px' }} />
              )}
              <span style={{
                flex: 1,
                minWidth: 0,
                wordBreak: 'break-word',
                color: isClickable ? '#333333' : '#374151',
                lineHeight: '1.3',
                textDecoration: isClickable ? 'underline' : 'none'
              }}>
                {displayLabel}
              </span>
              <span style={{ color, fontWeight: '600', flexShrink: 0 }}>
                {formatNumber(count)}
              </span>
              <span style={{ color: '#9ca3af', fontSize: '0.85em', flexShrink: 0 }}>
                ({percentage}%)
              </span>
              {direction === 'right' && (
                <ArrowRight size={12} style={{ color: '#9ca3af', flexShrink: 0, marginTop: '2px' }} />
              )}
            </div>
          );
        })}
        {items.length > 5 && (
          <div style={{
            padding: '4px 10px',
            fontSize: '0.8em',
            color: '#9ca3af',
            fontStyle: 'italic'
          }}>
            +{items.length - 5} autres
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="matomo-transitions-chart" style={{
      marginTop: '24px',
      backgroundColor: '#ffffff',
      borderRadius: '12px',
      border: '1px solid #e5e7eb',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 20px',
        borderBottom: '1px solid #e5e7eb',
        backgroundColor: '#FAF9F5',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        <h3 style={{
          margin: 0,
          fontSize: '1.1em',
          fontWeight: '600',
          color: '#1f2937',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <RefreshCw size={20} style={{ color: '#1a1a1a' }} />
          Transitions
        </h3>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* Sélecteur de page */}
          <div style={{ position: 'relative', width: '250px', flexShrink: 0 }}>
            <Search size={16} style={{
              position: 'absolute',
              left: '10px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#9ca3af',
              zIndex: 1
            }} />
            <input
              type="text"
              placeholder="Rechercher..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px 8px 32px',
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                fontSize: '0.9em',
                boxSizing: 'border-box'
              }}
            />
          </div>
          {toggleClientModuleVisibility && (
            <ModuleVisibilityToggle
              isVisible={isClientModuleVisible ? isClientModuleVisible(moduleId) : true}
              onToggle={toggleClientModuleVisibility}
              userRole={userRole}
              moduleId={moduleId}
              title="Transitions de pages"
            />
          )}
        </div>
      </div>

      {/* Liste des pages ou visualisation */}
      <div style={{ padding: '20px' }}>
        {!selectedPage ? (
          // Liste des pages pour sélection
          <div>
            <p style={{ margin: '0 0 16px', color: '#6b7280', fontSize: '0.9em' }}>
              Sélectionnez une page pour voir les transitions
            </p>
            {loadingPages ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                Chargement des pages...
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gap: '8px',
                maxHeight: '400px',
                overflowY: 'auto'
              }}>
                {filteredPages.slice(0, 20).map((page, index) => (
                  <div
                    key={index}
                    onClick={() => selectPage(page.url || page.label, page.label)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '12px 16px',
                      backgroundColor: '#f9fafb',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      transition: 'background-color 0.15s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                  >
                    <span style={{
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      color: '#374151'
                    }}>
                      {page.label}
                    </span>
                    <span style={{
                      marginLeft: '16px',
                      color: '#6b7280',
                      fontSize: '0.9em'
                    }}>
                      {formatNumber(page.pageviews)} vues
                    </span>
                  </div>
                ))}
                {filteredPages.length === 0 && !loadingPages && (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
                    Aucune page trouvée
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          // Visualisation des transitions
          <div>
            {/* Bouton retour */}
            <button
              onClick={() => {
                setSelectedPage('');
                setTransitions(null);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                marginBottom: '16px',
                backgroundColor: '#f3f4f6',
                border: 'none',
                borderRadius: '6px',
                color: '#6b7280',
                cursor: 'pointer',
                fontSize: '0.85em'
              }}
            >
              <ArrowLeft size={14} />
              Choisir une autre page
            </button>

            {loading ? (
              <div style={{ textAlign: 'center', padding: '60px', color: '#6b7280' }}>
                Chargement des transitions...
              </div>
            ) : error ? (
              <div style={{
                padding: '20px',
                backgroundColor: '#fef2f2',
                borderRadius: '8px',
                color: '#dc2626',
                textAlign: 'center'
              }}>
                {error}
              </div>
            ) : transitions ? (
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 220px 1fr',
                gap: '20px',
                alignItems: 'start',
                minWidth: 0
              }}>
                {/* Colonne gauche - D'où viennent les visiteurs */}
                <div style={{ minWidth: 0, overflow: 'hidden' }}>
                  <h4 style={{
                    margin: '0 0 16px',
                    fontSize: '0.95em',
                    fontWeight: '600',
                    color: '#059669',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <ArrowRight size={16} />
                    Entrées
                  </h4>

                  <TransitionSection
                    title="Pages précédentes"
                    icon={Link2}
                    items={getPreviousPages()}
                    color="#059669"
                    direction="left"
                    clickable={true}
                  />

                  <TransitionSection
                    title="Recherches internes"
                    icon={Search}
                    items={getPreviousSiteSearches()}
                    color="#059669"
                    direction="left"
                  />

                  <TransitionSection
                    title="Référents externes"
                    icon={Globe}
                    items={getReferrers()}
                    color="#059669"
                    direction="left"
                    countKey="visits"
                  />

                  {transitions.directEntries > 0 && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 10px',
                      backgroundColor: '#f0fdf4',
                      borderRadius: '6px',
                      fontSize: '0.85em'
                    }}>
                      <LogIn size={14} style={{ color: '#059669' }} />
                      <span style={{ flex: 1, color: '#374151' }}>Entrées directes</span>
                      <span style={{ color: '#059669', fontWeight: '600' }}>
                        {formatNumber(transitions.directEntries)}
                      </span>
                    </div>
                  )}
                </div>

                {/* Colonne centrale - Page analysée */}
                <div style={{
                  backgroundColor: '#1a1a1a',
                  borderRadius: '12px',
                  padding: '16px',
                  color: '#ffffff',
                  textAlign: 'center',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    fontSize: '0.8em',
                    opacity: 0.8,
                    marginBottom: '8px'
                  }}>
                    Page analysée
                  </div>
                  <div style={{
                    fontSize: '0.9em',
                    fontWeight: '600',
                    marginBottom: '16px',
                    wordBreak: 'break-word',
                    lineHeight: '1.3'
                  }}>
                    {selectedPageLabel}
                  </div>

                  <div style={{
                    display: 'grid',
                    gap: '12px'
                  }}>
                    <div style={{
                      backgroundColor: 'rgba(255,255,255,0.15)',
                      borderRadius: '8px',
                      padding: '12px'
                    }}>
                      <div style={{ fontSize: '1.5em', fontWeight: '700' }}>
                        {formatNumber(totalPageviews)}
                      </div>
                      <div style={{ fontSize: '0.8em', opacity: 0.8 }}>Pages vues</div>
                    </div>

                    {totalLoops > 0 && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        fontSize: '0.85em',
                        opacity: 0.9
                      }}>
                        <RefreshCw size={14} />
                        {formatNumber(totalLoops)} rechargements
                      </div>
                    )}
                  </div>
                </div>

                {/* Colonne droite - Où vont les visiteurs */}
                <div style={{ minWidth: 0, overflow: 'hidden' }}>
                  <h4 style={{
                    margin: '0 0 16px',
                    fontSize: '0.95em',
                    fontWeight: '600',
                    color: '#dc2626',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    Sorties
                    <ArrowRight size={16} />
                  </h4>

                  <TransitionSection
                    title="Pages suivantes"
                    icon={Link2}
                    items={getFollowingPages()}
                    color="#dc2626"
                    direction="right"
                    clickable={true}
                  />

                  <TransitionSection
                    title="Recherches internes"
                    icon={Search}
                    items={getFollowingSiteSearches()}
                    color="#dc2626"
                    direction="right"
                  />

                  <TransitionSection
                    title="Liens externes"
                    icon={ExternalLink}
                    items={getOutlinks()}
                    color="#dc2626"
                    direction="right"
                  />

                  <TransitionSection
                    title="Téléchargements"
                    icon={Download}
                    items={getDownloads()}
                    color="#dc2626"
                    direction="right"
                  />

                  {totalExits > 0 && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 10px',
                      backgroundColor: '#fef2f2',
                      borderRadius: '6px',
                      fontSize: '0.85em'
                    }}>
                      <LogOut size={14} style={{ color: '#dc2626' }} />
                      <span style={{ flex: 1, color: '#374151' }}>Sorties du site</span>
                      <span style={{ color: '#dc2626', fontWeight: '600' }}>
                        {formatNumber(totalExits)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

export default MatomoTransitionsChart;
